import { SEOHead } from '../components/SEOHead';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ScrollReveal';
import { CTABanner } from '../components/CTABanner';
import { ServiceFAQ } from '../components/ServiceFAQ';
import { Check } from 'lucide-react';

const d2cContent = [
  'Monthly Instagram content calendars',
  'AI-generated product and lifestyle visuals',
  'UGC-style brand posts',
  'Reels scripts and content ideas',
];

const employerContent = [
  'Culture and team content',
  'Day-in-the-life posts',
  'Hiring campaign creatives',
  'LinkedIn employer brand content',
];

const whoFor = [
  'D2C brands who need consistent Instagram content without shoot budgets',
  'Growing companies — 50 to 500 employees — trying to attract better talent',
  'HR teams and talent acquisition leads',
];

const faqs = [
  { question: 'What is employer branding content?', answer: 'Employer branding content is marketing material designed to attract potential employees. It includes culture stories, team spotlights, day-in-the-life videos, hiring campaign creatives, and LinkedIn posts that showcase what it is like to work at your company. Good employer branding reduces hiring costs and attracts better talent.' },
  { question: 'How many posts do you produce per month?', answer: 'We offer flexible content packages ranging from 12 to 30+ posts per month depending on your needs and budget. Each package includes a mix of static posts, carousels, Reels scripts, and story content. We create a custom content calendar tailored to your brand.' },
  { question: 'Do you manage the Instagram account or just produce content?', answer: 'We primarily focus on content production — creating the actual posts, visuals, and copy. Account management (posting, community management, DM responses) can be discussed separately. Our core strength is producing high-quality content consistently.' },
  { question: 'What is the difference between social media management and content production?', answer: 'Social media management includes account handling, scheduling, community management, and analytics. Content production is specifically about creating the content itself — visuals, copy, videos. We specialize in content production, which is often the biggest bottleneck for brands.' },
  { question: 'How do you produce content without a shoot?', answer: 'We use a combination of AI-generated visuals, brand asset libraries, UGC-style content from our creator network, and design tools to create professional Instagram content. This eliminates the need for expensive studio shoots while maintaining high quality and brand consistency.' },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Social Media Content and Employer Branding",
  "provider": { "@type": "Organization", "name": "Thriveit Media", "url": "https://thriveitmedia.in" },
  "description": "Instagram content pipelines for D2C brands and employer branding content for growing companies. No shoot days required.",
  "areaServed": "Worldwide"
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({
    "@type": "Question",
    "name": f.question,
    "acceptedAnswer": { "@type": "Answer", "text": f.answer }
  }))
};

export default function SocialMediaContentPage() {
  return (
    <>
      <SEOHead
        title="Social Media Content Agency | Instagram Content & Employer Branding | Thriveit Media"
        description="Thriveit Media produces Instagram content and employer branding content for D2C brands and growing companies. No shoot days. AI-assisted production."
        canonical="https://thriveitmedia.in/services/social-media-content"
        schema={[serviceSchema, faqSchema]}
      />

      {/* Hero */}
      <section className="py-32 md:py-40 relative">
        <div className="absolute inset-0 grid-pattern" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-6">Social Media Content</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none text-[#F5F5F0] max-w-5xl mb-8">
              Social Media Content Agency for D2C Brands and Employer Branding
            </h1>
          </ScrollReveal>
        </div>
      </section>

      {/* Instagram Content */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-8">Full Instagram Content Pipelines — Without the Shoot</h2>
            <p className="text-base md:text-lg font-body font-light text-[#888880] leading-relaxed max-w-3xl">
              Your Instagram feed is often the first place a potential customer goes after seeing your ad. If the content looks inconsistent or outdated — you lose them before they visit your website. We produce complete monthly Instagram content pipelines that keep your feed looking sharp, on-brand, and consistent — without the cost or time of traditional shoot cycles. We use AI image generation, AI video tools, and UGC-style production techniques to create content that looks like it came from a high-quality brand shoot.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Employer Branding */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-8">Employer Branding Content</h2>
            <p className="text-base md:text-lg font-body font-light text-[#888880] leading-relaxed max-w-3xl">
              In 2026, job seekers do not just read job descriptions. They research companies — on LinkedIn, on Instagram, on Glassdoor. They look for leadership behaviour, team culture, and growth opportunities. If your company does not have strong employer brand content, you are losing the best candidates before they even apply. As an employer branding content agency, we help companies build content that attracts top talent.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* What We Produce */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">What We Produce</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <ScrollReveal>
              <h3 className="font-heading text-xl font-bold text-[#FF5C35] mb-6">For D2C Brands</h3>
              <div className="space-y-3">
                {d2cContent.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#FF5C35] mt-1 flex-shrink-0" />
                    <p className="text-base font-body font-light text-[#888880]">{item}</p>
                  </div>
                ))}
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.15}>
              <h3 className="font-heading text-xl font-bold text-[#FF5C35] mb-6">For Employer Branding</h3>
              <div className="space-y-3">
                {employerContent.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#FF5C35] mt-1 flex-shrink-0" />
                    <p className="text-base font-body font-light text-[#888880]">{item}</p>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">Who This Is For</h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whoFor.map((item, i) => (
              <StaggerItem key={i}>
                <div className="bg-[#111111] border border-white/5 p-8 h-full">
                  <span className="text-4xl font-heading font-extrabold text-white/5">{String(i + 1).padStart(2, '0')}</span>
                  <p className="text-base font-body font-light text-[#888880] mt-4 leading-relaxed">{item}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <CTABanner
        heading="Book a Free Content Strategy Call"
        subtext="Let's build a content pipeline that keeps your Instagram feed and employer brand consistently strong — without shoot days."
        buttonText="Book a Free Strategy Call"
      />
    </>
  );
}
